import { config } from './constants';

export interface User {
  id: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  imageUrl: string | null;
}

export interface Game {
  id: number;
  type: string;
  status: 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'ABANDONED';
  createdAt: string;
  updatedAt: string;
  footyGame: {
    id: number;
    gameId: number;
    scoreTeamA: number;
    scoreTeamB: number;
    duration: number;
  };
  participations: {
    id: number;
    gameId: number;
    userId: string;
    role: 'team_a' | 'team_b';
    user: {
      id: string;
      username: string | null;
      firstName: string | null;
      lastName: string | null;
      imageUrl: string | null;
    };
  }[];
}

export interface CreateGameResponse extends Game {}

export interface FilterGamesParams {
  status?: 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'ABANDONED';
  userId?: string;
}

export interface UpdateGameParams {
  scoreTeamA?: number;
  scoreTeamB?: number;
  duration?: number;
}

export interface JoinGameParams {
  userId: string;
}

export interface AddParticipantParams {
  userId: string;
  role: 'team_a' | 'team_b';
}

class GamesAPI {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${config.url}/games`;
  }

  async createGame(userId: string): Promise<CreateGameResponse> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create game: ${response.statusText}`);
    }

    return response.json();
  }

  async getGames(filters?: FilterGamesParams): Promise<Game[]> {
    const params = new URLSearchParams();
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.userId) {
      params.append('userId', filters.userId);
    }

    const url = params.toString() ? `${this.baseUrl}?${params}` : this.baseUrl;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch games: ${response.statusText}`);
    }

    return response.json();
  }

  async getGame(id: number): Promise<Game> {
    const response = await fetch(`${this.baseUrl}/${id}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch game: ${response.statusText}`);
    }

    return response.json();
  }

  async joinGame(gameId: number, params: JoinGameParams): Promise<Game> {
    const response = await fetch(`${this.baseUrl}/${gameId}/join`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(`Failed to join game: ${response.statusText}`);
    }

    return response.json();
  }

  async addParticipant(gameId: number, params: AddParticipantParams): Promise<Game> {
    const response = await fetch(`${this.baseUrl}/${gameId}/participants`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(`Failed to add participant: ${response.statusText}`);
    }

    return response.json();
  }

  async startGame(gameId: number): Promise<Game> {
    const response = await fetch(`${this.baseUrl}/${gameId}/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to start game: ${response.statusText}`);
    }

    return response.json();
  }

  async updateGame(gameId: number, params: UpdateGameParams): Promise<Game> {
    const response = await fetch(`${this.baseUrl}/${gameId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(`Failed to update game: ${response.statusText}`);
    }

    return response.json();
  }

  async completeGame(gameId: number): Promise<Game> {
    const response = await fetch(`${this.baseUrl}/${gameId}/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to complete game: ${response.statusText}`);
    }

    return response.json();
  }

  async abandonGame(gameId: number): Promise<Game> {
    const response = await fetch(`${this.baseUrl}/${gameId}/abandon`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to abandon game: ${response.statusText}`);
    }

    return response.json();
  }
}

class UsersAPI {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${config.url.replace('/footy', '')}/users`;
  }

  async getAll(): Promise<User[]> {
    const response = await fetch(this.baseUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch users: ${response.statusText}`);
    }

    return response.json();
  }
}

export const gamesAPI = new GamesAPI();
export const usersAPI = new UsersAPI();
